import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Archive,
  CircleAlert,
  Gauge,
  Lightbulb,
  Plus,
  PlusCircle,
  RotateCw,
  Search,
  TriangleAlert,
  Warehouse,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import storageLocationService from "@/services/storageLocationService";
import { showError, showInfo } from "@/store/errorStore";
import StorageLocationCard from "./StorageLocationCard";
import StorageLocationFormModal from "./StorageLocationFormModal";
import {
  CRITICAL_USAGE,
  STATUS,
  formatUnits,
  getOccupancy,
  getStatusKey,
  getUsage,
  normalize,
} from "./storageLocationUtils";

const FILTERS = {
  all: { label: "Todos", match: () => true },
  critical: { label: "Críticos", match: (o) => o.statusKey === "critical" },
  warning: { label: "Atenção", match: (o) => o.statusKey === "warning" },
  normal: { label: "Disponíveis", match: (o) => o.statusKey === "normal" },
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Erros HTTP já abrem o modal global; aqui só falhas locais (sem resposta da API) */
const showLocalError = (error, fallback) => {
  if (!error?.response && !error?.request) {
    showError(error?.message || fallback, "Locais de Armazenamento");
  }
};

function MetricCard({ label, icon: Icon, iconClass, onClick, active, children }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      aria-pressed={onClick ? active : undefined}
      className={cn(
        "flex min-w-0 flex-col justify-between rounded-xl border bg-card p-3.5 text-left shadow-soft transition-all",
        onClick && "hover:shadow-ambient active:scale-[0.99]",
        active && "border-primary ring-2 ring-primary/15"
      )}
    >
      <span className="flex w-full items-center justify-between gap-2">
        <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className={cn("flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full", iconClass)}>
          <Icon className="h-4 w-4" />
        </span>
      </span>
      <span className="mt-2 block min-w-0">{children}</span>
    </Tag>
  );
}

function FilterChip({ active, onClick, count, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-semibold shadow-sm transition-colors",
        active ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground"
      )}
    >
      {children}
      <span
        className={cn(
          "flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px]",
          active ? "bg-white/20" : "bg-surface-container"
        )}
      >
        {count}
      </span>
    </button>
  );
}

/**
 * Gestão de Locais de Armazenamento via /storage-locations.
 * A ocupação (used_capacity) vem da API: soma do saldo dos lotes no local.
 */
function StorageLocationsManagement() {
  const [storageLocations, setStorageLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  /**
   * `background`: recarrega sem esqueleto (após salvar/excluir); se falhar,
   * o modal global avisa e a lista atual continua na tela. Na carga normal o
   * erro aparece no lugar da lista, com "Tentar novamente".
   */
  const loadData = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    try {
      const data = await storageLocationService.getAllStorageLocations({ silent: !background });
      setStorageLocations(Array.isArray(data) ? data : []);
      setLoadError(false);
    } catch (error) {
      if (!background) setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Ocupação e texto de busca calculados uma vez por carga
  const entries = useMemo(
    () =>
      storageLocations.map((location) => ({
        location,
        ...getOccupancy(location),
        search: normalize(`${location.name} ${location.equipment_name ?? ""} ${location.id}`),
      })),
    [storageLocations]
  );

  const stats = useMemo(() => {
    // Taxa geral só considera locais com capacidade definida
    const measured = entries.filter((e) => e.capacity > 0);
    const capacity = measured.reduce((sum, e) => sum + e.capacity, 0);
    const used = measured.reduce((sum, e) => sum + e.used, 0);
    const usage = getUsage(capacity, used);
    return {
      total: entries.length,
      capacity,
      used,
      usage,
      status: STATUS[getStatusKey(usage)],
      critical: entries.filter((e) => e.statusKey === "critical").length,
    };
  }, [entries]);

  // Busca: base das contagens dos chips e da lista
  const scoped = useMemo(() => {
    const term = normalize(searchQuery);
    return term ? entries.filter((e) => e.search.includes(term)) : entries;
  }, [entries, searchQuery]);

  const counts = useMemo(() => {
    const c = {};
    Object.entries(FILTERS).forEach(([key, f]) => {
      c[key] = scoped.filter(f.match).length;
    });
    return c;
  }, [scoped]);

  const shown = useMemo(
    () => scoped.filter((FILTERS[filter] || FILTERS.all).match).map((e) => e.location),
    [scoped, filter]
  );

  const hasFilters = Boolean(searchQuery.trim()) || filter !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setFilter("all");
  };

  /* ---------- Ações ---------- */

  const openCreate = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const openEdit = useCallback((location) => {
    setEditingItem(location);
    setShowModal(true);
  }, []);

  // editingItem fica até a próxima abertura: limpar aqui trocaria o título
  // para "Novo Local" durante a animação de fechamento
  const handleModalChange = (open) => setShowModal(open);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editingItem) {
        await storageLocationService.updateStorageLocation(editingItem.id, payload);
        showInfo(`"${payload.name}" atualizado`, "Locais de Armazenamento");
      } else {
        await storageLocationService.createStorageLocation(payload);
        showInfo(`"${payload.name}" cadastrado`, "Locais de Armazenamento");
      }
      handleModalChange(false);
      await loadData({ background: true });
    } catch (error) {
      showLocalError(error, "Erro ao salvar o local de armazenamento");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    setDeletingId(target.id);
    try {
      await storageLocationService.deleteStorageLocation(target.id);
      showInfo(`"${target.name}" foi excluído`, "Locais de Armazenamento");
      await loadData({ background: true });
    } catch (error) {
      showLocalError(error, "Erro ao excluir o local de armazenamento");
    } finally {
      setDeletingId(null);
    }
  };

  const initialLoading = loading && storageLocations.length === 0;
  const deleteTargetUsed = deleteTarget ? getOccupancy(deleteTarget).used : 0;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      {/* Título e ação principal */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-xl font-bold tracking-tight text-primary md:text-2xl">
            Locais de Armazenamento
          </h1>
          <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-muted-foreground md:text-sm">
            <span className="h-2 w-2 flex-shrink-0 rounded-full bg-brand-pistachio" aria-hidden="true" />
            {initialLoading ? "Carregando locais..." : plural(stats.total, "local cadastrado", "locais cadastrados")}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex h-10 flex-shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95"
        >
          <Plus className="h-4 w-4" />
          Novo Local
        </button>
      </div>

      {/* Métricas (2x2 no celular, 4 colunas no desktop) */}
      {initialLoading ? (
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[112px] rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          <MetricCard label="Total de Locais" icon={Warehouse} iconClass="bg-surface-container text-primary">
            <span className="block font-display text-xl font-bold text-foreground">{stats.total}</span>
            <span className="block truncate text-[11px] text-muted-foreground">Locais cadastrados</span>
          </MetricCard>
          <MetricCard label="Capacidade Total" icon={Archive} iconClass="bg-surface-container text-primary">
            <span className="block truncate font-display text-xl font-bold text-foreground">
              {formatUnits(stats.capacity)}
            </span>
            <span className="block truncate text-[11px] text-muted-foreground">Unidades máx.</span>
          </MetricCard>
          <MetricCard
            label="Taxa de Ocupação"
            icon={stats.usage !== null && stats.usage >= CRITICAL_USAGE ? TriangleAlert : Gauge}
            iconClass={cn(
              stats.usage !== null && stats.usage >= CRITICAL_USAGE
                ? "bg-destructive/10 text-destructive"
                : "bg-surface-container text-primary"
            )}
          >
            <span className={cn("block font-display text-xl font-bold", stats.status.text)}>
              {stats.usage === null ? "—" : `${stats.usage}%`}
            </span>
            <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-surface-high">
              <span
                className={cn("block h-full rounded-full transition-all duration-500", stats.status.bar)}
                style={{ width: `${Math.min(stats.usage ?? 0, 100)}%` }}
              />
            </span>
            <span className="mt-1 block truncate text-[11px] text-muted-foreground">
              {formatUnits(stats.used)} de {formatUnits(stats.capacity)} usados
            </span>
          </MetricCard>
          <MetricCard
            label="Locais Críticos"
            icon={CircleAlert}
            iconClass={stats.critical ? "bg-destructive/10 text-destructive" : "bg-secondary text-primary"}
            onClick={() => setFilter((current) => (current === "critical" ? "all" : "critical"))}
            active={filter === "critical"}
          >
            <span
              className={cn("block font-display text-xl font-bold", stats.critical ? "text-destructive" : "text-primary")}
            >
              {stats.critical}
            </span>
            <span className="block truncate text-[11px] text-muted-foreground">≥ {CRITICAL_USAGE}% de ocupação</span>
          </MetricCard>
        </div>
      )}

      {/* Busca */}
      <div className="relative flex items-center">
        <Search className="pointer-events-none absolute left-3.5 h-4 w-4 text-muted-foreground" />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar por nome ou equipamento..."
          aria-label="Buscar locais de armazenamento"
          className="h-11 w-full rounded-full border border-border bg-card pl-10 pr-10 text-base text-foreground shadow-sm transition-colors placeholder:text-muted-foreground/80 focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25 md:text-sm"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            aria-label="Limpar busca"
            className="absolute right-2 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Chips de filtro (scroll horizontal no celular) */}
      <div
        className="no-scrollbar -mx-4 flex items-center gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0"
        role="group"
        aria-label="Filtrar locais por ocupação"
      >
        {Object.entries(FILTERS).map(([key, f]) => (
          <FilterChip key={key} active={filter === key} count={counts[key] || 0} onClick={() => setFilter(key)}>
            {f.label}
          </FilterChip>
        ))}
      </div>

      {/* Lista */}
      {initialLoading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[236px] rounded-2xl" />
          ))}
        </div>
      ) : loadError && storageLocations.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <Warehouse className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">
            Não foi possível carregar os locais
          </p>
          <p className="mt-1 text-sm text-muted-foreground">Verifique a conexão e tente de novo.</p>
          <button
            type="button"
            onClick={() => loadData()}
            className="mt-4 flex h-9 items-center gap-1 rounded-full border border-border bg-surface px-3.5 text-sm font-semibold text-primary transition-colors hover:bg-muted"
          >
            <RotateCw className="h-4 w-4" />
            Tentar novamente
          </button>
        </div>
      ) : shown.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <Warehouse className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">
            {hasFilters ? "Nenhum local encontrado" : "Nenhum local cadastrado"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {hasFilters
              ? "Tente outra busca ou limpe os filtros."
              : "Cadastre freezers, prateleiras e bandejas para organizar o estoque."}
          </p>
          <button
            type="button"
            onClick={hasFilters ? clearFilters : openCreate}
            className={cn(
              "mt-4 flex h-9 items-center gap-1 rounded-full px-3.5 text-sm font-semibold transition-colors",
              hasFilters
                ? "border border-border bg-surface text-primary hover:bg-muted"
                : "bg-primary text-primary-foreground hover:bg-brand-dark"
            )}
          >
            {hasFilters ? <X className="h-4 w-4" /> : <PlusCircle className="h-4 w-4" />}
            {hasFilters ? "Limpar filtros" : "Novo local"}
          </button>
        </div>
      ) : (
        <section className="space-y-3" aria-label="Lista de locais de armazenamento">
          <div className="flex items-center justify-between gap-3 px-1">
            <span className="font-display text-[13px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Lista de Locais
            </span>
            <span className="flex items-center gap-2 text-xs text-muted-foreground">
              Mostrando {shown.length} de {stats.total}
              {hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[13px] font-semibold text-primary hover:underline"
                >
                  Limpar filtros
                </button>
              )}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {shown.map((location) => (
              <StorageLocationCard
                key={location.id}
                location={location}
                editing={showModal && editingItem?.id === location.id}
                deleting={deletingId === location.id}
                onEdit={openEdit}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        </section>
      )}

      {/* Dica de gestão */}
      {!initialLoading && stats.total > 0 && (
        <aside className="flex items-start gap-3 rounded-xl bg-secondary/50 p-3.5">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
            <Lightbulb className="h-4 w-4" />
          </span>
          <span>
            <span className="block text-[13px] font-semibold text-primary">Dica de Gestão</span>
            <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
              {stats.critical
                ? `${plural(stats.critical, "local está", "locais estão")} acima de ${CRITICAL_USAGE}% de ocupação. Redistribua os lotes para manter a circulação de ar frio e a conservação dos produtos.`
                : `Mantenha a ocupação dos freezers abaixo de ${CRITICAL_USAGE}% para garantir a circulação uniforme de ar frio e a conservação dos produtos.`}
            </span>
          </span>
        </aside>
      )}

      <StorageLocationFormModal
        open={showModal}
        onOpenChange={handleModalChange}
        storageLocation={editingItem}
        storageLocations={storageLocations}
        onSave={handleSave}
        loading={saving}
      />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir local de armazenamento?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  <span className="font-semibold text-foreground">{deleteTarget?.name}</span> será removido
                  permanentemente. Esta ação não pode ser desfeita.
                </p>
                {deleteTargetUsed > 0 && (
                  <p className="flex items-start gap-1.5 rounded-lg bg-terra-soft p-2.5 text-xs font-medium text-terra">
                    <TriangleAlert className="mt-px h-4 w-4 flex-shrink-0" />
                    Há {formatUnits(deleteTargetUsed)} un em estoque neste local. Mova os lotes para outro local
                    antes de excluir.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default StorageLocationsManagement;
